import { useEffect, useRef, useMemo } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import { useToken } from '../../hooks/useToken';
import DashboardWrapper from '../../layout/DashboardWrapper';
import StatisticsCard from '../../components/cards/StatisticsCard';
import { Award, Zap, TrendingUp, Star, Link, ChevronLeft, ChevronRight } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import StatisticsCardSkeleton from '../../components/skeletons/StatisticsCardSkeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { XpTimelineChart } from '../../components/sections/XPTimelineChart';
import LevelBreakdown from '../../components/sections/LevelBreakdown';
import XPActivityCalendarCard from '../../components/sections/XPQuarterlyCard';
import OverallXpRankSection from '../../components/sections/OverallXpRankSection';
import { useNavigate, useSearch } from '@tanstack/react-router';
import BadgeCard from '../../components/cards/BadgeCard';
import QuickAccessLinkItem from '../../components/cards/QuickAccessLinkItem';
import XpTimelineChartSkeleton from '../../components/skeletons/XpTimelineChartSkeleton';
import { DataNotFoundSection } from '../../components/sections/DataNotFoundSection';
import LevelBreakdownSkeleton from '../../components/skeletons/LevelBreakdownSkeleton';
import XPActivityCalendarCardSkeleton from '../../components/skeletons/XPActivityCalendarCardSkeleton';

const AchievementsPage = () => {
    // Derived from queries below — don't keep duplicate local copies

    const { user } = useUser();
    const token = useToken();
    console.log('AchievementsPage:init', { token, userId: user?.id });
    const navigate = useNavigate({ from: '/achievements' });
    const { tab } = useSearch({ from: '/achievements' });

    const handleTabChange = newTab => {
        // Update the URL search parameter without reloading the page
        navigate({ search: prev => ({ ...prev, tab: newTab }) });
    };

    const userXpQuery = useQuery({
        queryKey: queryKeys.userXp(user?.id),
        queryFn: () => {
            console.log('AchievementsPage:fetch userXp', {
                enabled: !!token && !!user?.id,
                userId: user?.id,
                hasToken: !!token,
            });
            return api.getUserXp(user.id, token || '');
        },
        enabled: !!token && !!user?.id,
        onSuccess: data => {
            console.debug('AchievementsPage:userXp success', data);
        },
        onError: err => {
            console.error('AchievementsPage:userXpQuery error', err);
        },
    });

    const leaderboardParams = { skip: 0, limit: 100 };
    const leaderboardQuery = useQuery({
        queryKey: queryKeys.cohortLeaderboard(leaderboardParams),
        queryFn: () => {
            console.log('AchievementsPage:fetch leaderboard', {
                enabled: !!token,
                params: leaderboardParams,
                hasToken: !!token,
            });
            return api.getCohortLeaderboard(token || '', leaderboardParams);
        },
        enabled: !!token,
        onSuccess: data => {
            console.log('AchievementsPage:leaderboard success', data);
        },
        onError: err => {
            console.error('AchievementsPage:leaderboardQuery error', err);
        },
    });

    const streakQuery = useQuery({
        queryKey: queryKeys.userStreak(user?.id),
        queryFn: () => {
            console.log('AchievementsPage:fetch streak', {
                enabled: !!token && !!user?.id,
                userId: user?.id,
                hasToken: !!token,
            });
            return api.getUserStreak(user.id, token || '');
        },
        enabled: !!token && !!user?.id,
        onSuccess: data => {
            console.log('AchievementsPage:streak success', data);
        },
        onError: err => {
            console.error('AchievementsPage:streakQuery error', err);
        },
    });

    const badgeQuery = useQuery({
        queryKey: queryKeys.userBadgeProgress(user?.id),
        queryFn: () => {
            console.log('AchievementsPage:fetch badges', {
                enabled: !!token && !!user?.id,
                userId: user?.id,
                hasToken: !!token,
            });
            return api.getUserBadgeProgress(user.id, token || '');
        },
        enabled: !!token && !!user?.id,
        onSuccess: data => {
            console.log('AchievementsPage:badges success', data);
        },
        onError: err => {
            console.error('AchievementsPage:badgeQuery error', err);
        },
    });

    const statsQuery = useQuery({
        queryKey: queryKeys.userStats(),
        queryFn: () => {
            console.log('AchievementsPage:fetch stats', { enabled: !!token, hasToken: !!token });
            return api.getUserStats(token || '');
        },
        enabled: !!token,
        onSuccess: data => {
            console.log('gamification:user-stats', data);
        },
        onError: err => {
            console.error('AchievementsPage:statsQuery error', err);
        },
    });

    useEffect(() => {
        document.title = 'My Achievements | Zindua LMS';
    }, []);

    const anyLoading =
        userXpQuery.isLoading ||
        leaderboardQuery.isLoading ||
        streakQuery.isLoading ||
        badgeQuery.isLoading ||
        statsQuery.isLoading;
    const isLoading = !!anyLoading;

    // Derive the data used in the UI from the query results (avoid duplicate local state)
    const userXpData = userXpQuery.data ?? null;

    const leaderboard = useMemo(() => {
        const data = leaderboardQuery.data;
        if (!data) return [];
        const allRaw = data.enrolments?.data || data.enrolments || [];
        const mapped = allRaw.map(u => ({
            id: u.user_id,
            name: `${u.user_first_name} ${u.user_last_name}`,
            course: u.course_name || u.course || '',
            points: u.total_points,
        }));

        const uniqueMap = new Map();
        mapped.forEach(u => {
            const key = `${u.id}-${u.course}`;
            uniqueMap.set(key, { ...u });
        });
        const uniqueArr = Array.from(uniqueMap.values());

        let rank = 1;
        let prevPoints = null;
        let prevRank = 1;
        const rankedLeaderboard = uniqueArr
            .sort((a, b) => (b.points || 0) - (a.points || 0))
            .map((u, idx) => {
                const pts = u.points || 0;
                if (idx === 0) {
                    rank = 1;
                    prevPoints = pts;
                    prevRank = 1;
                } else if (pts === prevPoints) {
                    rank = prevRank;
                } else {
                    rank = idx + 1;
                    prevPoints = pts;
                    prevRank = rank;
                }
                return { ...u, rank };
            });

        return rankedLeaderboard;
    }, [leaderboardQuery.data]);

    const userStreakData = useMemo(() => {
        const defaultStreak = { currentStreak: 0, longestStreak: 0, streak_string: '' };
        const data = streakQuery.data?.user_streak;
        if (!data || !data.streak_string) return defaultStreak;

        const xpPerDay = data.streak_string.split(',').map(Number);

        const getDayOfYear = date => {
            const start = new Date(date.getFullYear(), 0, 0);
            const diff = date - start;
            const oneDay = 1000 * 60 * 60 * 24;
            return Math.floor(diff / oneDay);
        };

        const todayIndex = getDayOfYear(new Date()) - 1;

        let currentStreak = 0;
        if (todayIndex < xpPerDay.length && xpPerDay[todayIndex] > 0) {
            currentStreak = 1;
            for (let i = todayIndex - 1; i >= 0; i--) {
                if (xpPerDay[i] > 0) {
                    currentStreak++;
                } else {
                    break;
                }
            }
        }

        let longestStreak = 0;
        let currentLongest = 0;
        for (let i = 0; i < xpPerDay.length; i++) {
            if (xpPerDay[i] > 0) {
                currentLongest++;
            } else {
                longestStreak = Math.max(longestStreak, currentLongest);
                currentLongest = 0;
            }
        }
        longestStreak = Math.max(longestStreak, currentLongest);

        return { currentStreak, longestStreak, streak_string: data.streak_string };
    }, [streakQuery.data]);

    const userBadgeProgress = badgeQuery.data?.badge_progress ?? {
        lessons: 0,
        projects: 0,
        quizzes: 0,
        project_quality: 0,
        quiz_quality: 0,
    };

    // Calculations from MyDashboard
    const currentUserPoints = userXpData?.user_xp?.total_xp || 0;
    const currentLevel = Math.trunc(Math.sqrt(currentUserPoints / 100)) + 1;
    const xpForNextLevel = Math.pow(currentLevel, 2) * 100;
    const pointsNeededToAdvance = Math.max(0, xpForNextLevel - currentUserPoints);
    const currentUserRank = leaderboard.find(p => p.id === user?.id);

    const isUserInTopFive = leaderboard.slice(0, 5).some(p => p.id === user?.id);

    const badgeTiers = {
        lesson: [
            { name: 'Initiate', requirement: 5 },
            { name: 'Rookie', requirement: 25 },
            { name: 'Specialist', requirement: 50 },
            { name: 'Pro', requirement: 100 },
            { name: 'Master', requirement: 200 },
            { name: 'Grandmaster', requirement: 400 },
            { name: 'Legend', requirement: 800 },
        ],
        project: [
            { name: 'Initiate', requirement: 3 },
            { name: 'Rookie', requirement: 7 },
            { name: 'Specialist', requirement: 15 },
            { name: 'Pro', requirement: 25 },
            { name: 'Master', requirement: 40 },
            { name: 'Grandmaster', requirement: 65 },
            { name: 'Legend', requirement: 100 },
        ],
        quiz: [
            { name: 'Initiate', requirement: 3 },
            { name: 'Rookie', requirement: 7 },
            { name: 'Specialist', requirement: 15 },
            { name: 'Pro', requirement: 25 },
            { name: 'Master', requirement: 40 },
            { name: 'Grandmaster', requirement: 65 },
            { name: 'Legend', requirement: 100 },
        ],
        'project-quality': [
            { name: 'Initiate', requirement: 1 },
            { name: 'Rookie', requirement: 5 },
            { name: 'Specialist', requirement: 10 },
            { name: 'Pro', requirement: 15 },
            { name: 'Master', requirement: 25 },
            { name: 'Grandmaster', requirement: 40 },
            { name: 'Legend', requirement: 65 },
        ],
        'quiz-perfectionist': [
            { name: 'Initiate', requirement: 1 },
            { name: 'Rookie', requirement: 5 },
            { name: 'Specialist', requirement: 10 },
            { name: 'Pro', requirement: 15 },
            { name: 'Master', requirement: 25 },
            { name: 'Grandmaster', requirement: 40 },
            { name: 'Legend', requirement: 65 },
        ],
        'streak-consistency': [
            { name: 'Initiate', requirement: 3 },
            { name: 'Rookie', requirement: 7 },
            { name: 'Specialist', requirement: 14 },
            { name: 'Pro', requirement: 30 },
            { name: 'Master', requirement: 60 },
            { name: 'Grandmaster', requirement: 100 },
            { name: 'Legend', requirement: 180 },
        ],
        level: [
            { name: 'Initiate', requirement: 2 },
            { name: 'Rookie', requirement: 3 },
            { name: 'Specialist', requirement: 5 },
            { name: 'Pro', requirement: 8 },
            { name: 'Master', requirement: 13 },
            { name: 'Grandmaster', requirement: 21 },
            { name: 'Legend', requirement: 34 },
        ],
    };

    const generateBadges = (type, progress) => {
        const tiers = badgeTiers[type];
        return tiers.map((tier, index) => {
            const previousTierRequirement = index > 0 ? tiers[index - 1].requirement : 0;
            let status = 'locked';
            let completedCount = 0;

            if (progress >= tier.requirement) {
                status = 'completed';
                completedCount = tier.requirement;
            } else if (progress > previousTierRequirement) {
                status = 'in-progress';
                completedCount = progress;
            }

            return {
                id: `${type}-${tier.name}`,
                title: `${
                    type === 'project-quality'
                        ? 'Project Quality'
                        : type === 'quiz-perfectionist'
                          ? 'Quiz Perfectionist'
                          : type === 'streak-consistency'
                            ? 'Streak'
                            : type === 'level'
                              ? 'Level'
                              : type.charAt(0).toUpperCase() + type.slice(1)
                } ${tier.name}`,
                totalItems: tier.requirement,
                completedItems: completedCount,
                status: status,
                badgeLevel: tier.name,
                type: type,
            };
        });
    };

    const lessonBadges = generateBadges('lesson', userBadgeProgress.lessons);
    const projectBadges = generateBadges('project', userBadgeProgress.projects);
    const quizBadges = generateBadges('quiz', userBadgeProgress.quizzes);
    const projectQualityBadges = generateBadges('project-quality', userBadgeProgress.project_quality);
    const quizPerfectionistBadges = generateBadges('quiz-perfectionist', userBadgeProgress.quiz_quality);
    const streakConsistencyBadges = generateBadges('streak-consistency', userStreakData.longestStreak);
    const levelBadges = generateBadges('level', currentLevel);

    const allBadges = [
        ...lessonBadges,
        ...projectBadges,
        ...quizBadges,
        ...projectQualityBadges,
        ...quizPerfectionistBadges,
        ...streakConsistencyBadges,
        ...levelBadges,
    ];

    // Swiper-based horizontal badge row showing 5 cards on wide screens
    const BadgeRow = ({ title, badges, sharedBy }) => {
        const swiperRef = useRef(null);

        return (
            <div>
                <div className="flex items-center justify-between mb-3 mt-6">
                    <h4 className="font-semibold">{title}</h4>
                </div>

                <div className="relative">
                    <button
                        type="button"
                        onClick={() => swiperRef.current?.slidePrev()}
                        aria-label={`Scroll ${title} left`}
                        className="hidden md:flex items-center justify-center p-2 bg-neutral-100 hover:bg-neutral-200 rounded-full absolute -left-1 top-1/2 -translate-y-1/2 -translate-x-1/2 z-20"
                    >
                        <ChevronLeft />
                    </button>

                    <Swiper
                        onSwiper={s => (swiperRef.current = s)}
                        modules={[Navigation]}
                        slidesPerView={5}
                        spaceBetween={16}
                        breakpoints={{
                            0: { slidesPerView: 1 },
                            640: { slidesPerView: 2 },
                            768: { slidesPerView: 3 },
                            1024: { slidesPerView: 4 },
                            1280: { slidesPerView: 5 },
                        }}
                        className="pb-3"
                    >
                        {badges.map(badge => (
                            <SwiperSlide key={badge.id} className="w-auto!">
                                <div className="px-1">
                                    <BadgeCard
                                        title={badge.title}
                                        totalItems={badge.totalItems}
                                        completedItems={badge.completedItems}
                                        status={badge.status}
                                        badgeLevel={badge.badgeLevel}
                                        type={badge.type}
                                        sharedBy={sharedBy}
                                    />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    <button
                        type="button"
                        onClick={() => swiperRef.current?.slideNext()}
                        aria-label={`Scroll ${title} right`}
                        className="hidden md:flex items-center justify-center p-2 bg-neutral-100 hover:bg-neutral-200 rounded-full absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-20"
                    >
                        <ChevronRight />
                    </button>
                </div>
            </div>
        );
    };

    const completedBadgesCount = allBadges.filter(badge => badge.status === 'completed').length;
    const totalBadgesCount = allBadges.length;

    return (
        <DashboardWrapper>
            <div className="w-full">
                <div className="flex flex-col gap-2 py-5">
                    <h1 className="">My Achievements</h1>
                    <p className="text-neutral-700">
                        Review your milestones, track your growth, and see how far you’ve advanced.
                    </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 my-5">
                    {isLoading ? (
                        <>
                            <StatisticsCardSkeleton />
                            <StatisticsCardSkeleton />
                            <StatisticsCardSkeleton />
                            <StatisticsCardSkeleton />
                        </>
                    ) : (
                        <>
                            <StatisticsCard
                                icon={<Award size={20} />}
                                title={`Level ${currentLevel}`}
                                value={`${currentUserPoints.toLocaleString()} XP`}
                                subtitle={
                                    <>
                                        <span className="text-success font-semibold">
                                            {pointsNeededToAdvance.toLocaleString()} XP
                                        </span>{' '}
                                        to get to Level {currentLevel + 1}
                                    </>
                                }
                            />
                            <StatisticsCard
                                icon={<TrendingUp size={20} />}
                                title="Leaderboard Rank"
                                value={currentUserRank ? `#${currentUserRank.rank}` : '-'}
                                subtitle={
                                    <>
                                        Out of <span className="text-success font-semibold">{leaderboard.length} students</span>
                                    </>
                                }
                            />
                            <StatisticsCard
                                icon={<Zap size={20} />}
                                title="Current Streak"
                                value={`${userStreakData.currentStreak} Day${userStreakData.currentStreak !== 1 ? 's' : ''}`}
                                subtitle={
                                    <>
                                        <span className="text-success font-semibold">
                                            {userStreakData.longestStreak} day
                                            {userStreakData.longestStreak !== 1 ? 's' : ''}
                                        </span>{' '}
                                        is the longest streak
                                    </>
                                }
                            />
                            <StatisticsCard
                                icon={<Star size={20} />}
                                title="Badges Earned"
                                value={completedBadgesCount}
                                subtitle={
                                    <>
                                        Out of <span className="text-success font-semibold">{totalBadgesCount}</span> badges
                                        available
                                    </>
                                }
                            />
                        </>
                    )}
                </div>

                <div className="mb-8 w-full">
                    <div className="w-full">
                        <Tabs value={tab === 'badges' ? 'badges' : 'overview'} onValueChange={handleTabChange} className="">
                            <div className="flex flex-col md:flex-row gap-4 justify-between md:max-w-3xl">
                                <TabsList className="justify-center max-md:w-full">
                                    <TabsTrigger value="overview" className="justify-center max-md:w-full capitalize">
                                        Overview
                                    </TabsTrigger>
                                    <TabsTrigger value="badges" className="justify-center max-md:w-full capitalize">
                                        Badges
                                    </TabsTrigger>
                                    {/* <TabsTrigger
                      value="skills"
                      className="justify-center max-md:w-full capitalize"
                    >
                      Skills
                    </TabsTrigger> */}
                                </TabsList>
                            </div>
                            <div>
                                <TabsContent value="overview">
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                                        {isLoading ? (
                                            <>
                                                <div className="w-full flex flex-col gap-4 lg:col-span-2">
                                                    <XpTimelineChartSkeleton />
                                                    <LevelBreakdownSkeleton />
                                                </div>
                                                <div className="lg:col-span-1 flex flex-col gap-6 w-full">
                                                    <XPActivityCalendarCardSkeleton />
                                                    <OverallXpRankSection isLoading={isLoading} />
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="w-full flex flex-col gap-4 lg:col-span-2">
                                                    {userStreakData.streak_string ? (
                                                        <XpTimelineChart data={userStreakData.streak_string} />
                                                    ) : (
                                                        <div className="bg-neutral-100 border border-neutral-300 rounded-xl p-4 h-115 flex items-center justify-center">
                                                            <DataNotFoundSection
                                                                title="No XP Timeline data available."
                                                                dashboard={true}
                                                            />
                                                        </div>
                                                    )}
                                                    <LevelBreakdown currentUserPoints={currentUserPoints} />
                                                </div>
                                                <div className="lg:col-span-1 flex flex-col gap-6 w-full">
                                                    {userStreakData.streak_string ? (
                                                        <XPActivityCalendarCard streakString={userStreakData.streak_string} />
                                                    ) : (
                                                        <div className="bg-neutral-100 border border-neutral-300 rounded-xl p-4 h-57.5 flex items-center justify-center">
                                                            <DataNotFoundSection
                                                                title="No activity streak data available."
                                                                dashboard={true}
                                                            />
                                                        </div>
                                                    )}

                                                    <OverallXpRankSection
                                                        isLoading={isLoading}
                                                        leaderboard={leaderboard}
                                                        user={user}
                                                        currentUserRank={currentUserRank}
                                                        isUserInTopFive={isUserInTopFive}
                                                    />
                                                    <div className="border border-neutral-300 rounded-xl">
                                                        <QuickAccessLinkItem
                                                            icon={<Link size={24} />}
                                                            title="Read our Gamification Docs"
                                                            description="Understand how it all works i.e. from earning XP/badges to streaks & leagues"
                                                            href="https://zinduaschool.notion.site/Gamification-Student-Guide-2e86cc64064f801bb3f1f771a53890b7"
                                                            isOnly={true}
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </TabsContent>
                                <TabsContent value="badges">
                                    <div className="py-8 flex flex-col gap-2">
                                        {/* Horizontal scroll rows for each badge category */}
                                        {[
                                            { title: 'Lesson Badges', items: lessonBadges },
                                            { title: 'Project Badges', items: projectBadges },
                                            { title: 'Quiz Badges', items: quizBadges },
                                            {
                                                title: 'Project Quality Badges',
                                                items: projectQualityBadges,
                                            },
                                            {
                                                title: 'Quiz Perfectionist Badges',
                                                items: quizPerfectionistBadges,
                                            },
                                            {
                                                title: 'Streak Badges',
                                                items: streakConsistencyBadges,
                                            },
                                            { title: 'Level Badges', items: levelBadges },
                                        ].map(section => (
                                            <BadgeRow
                                                key={section.title}
                                                title={section.title}
                                                badges={section.items}
                                                sharedBy={user?.fullName || user?.firstName}
                                            />
                                        ))}
                                    </div>
                                </TabsContent>
                                <TabsContent value="skills"></TabsContent>
                            </div>
                        </Tabs>
                    </div>
                </div>
            </div>
        </DashboardWrapper>
    );
};

export default AchievementsPage;
